import * as React from 'react';
import {
  Grid,
  Typography,
  Container,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Pagination,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Paper,
  Divider,
} from '@mui/material';
import TuneIcon from '@mui/icons-material/Tune';
import SortIcon from '@mui/icons-material/Sort';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ProductCard from '../components/ProductCard';
import '../App.css';
import { useNotifier } from '../context/NotificationProvider';
import { apiClient } from '../services/apiClient';
import { useLocation } from 'react-router-dom';
import { trackTikTokEvent } from '../analytics/tiktokPixel';
import { trackMetaEvent } from '../analytics/metaPixel';
import { TIKTOK_CURRENCY } from '../analytics/tiktokEvents';

const itemsPerPage = 6;

function Shop({ products, addToCart, loading, error }) {
  const [categoryFilter, setCategoryFilter] = React.useState('all');
  const [viewFilter, setViewFilter] = React.useState('all');
  const [page, setPage] = React.useState(1);
  const [animatedCards, setAnimatedCards] = React.useState([]);
  const [sortOption, setSortOption] = React.useState('featured');
  const [searchQuery, setSearchQuery] = React.useState('');
  const [searchProducts, setSearchProducts] = React.useState(null);
  const [searchTotal, setSearchTotal] = React.useState(null);
  const [searchLoading, setSearchLoading] = React.useState(false);
  const [searchError, setSearchError] = React.useState(null);
  const [randomProducts, setRandomProducts] = React.useState([]);
  const [randomTotal, setRandomTotal] = React.useState(0);
  const [randomLoading, setRandomLoading] = React.useState(false);
  const [randomError, setRandomError] = React.useState(null);
  const randomSeed = React.useRef(`${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const { notify } = useNotifier();
  const location = useLocation();

  const capitalizeCategory = React.useCallback(category => (category ? category.charAt(0).toUpperCase() + category.slice(1) : ''), []);

  const uniqueCategories = React.useMemo(() => {
    const bucket = new Set();
    products.forEach(product => {
      if (product?.category) {
        bucket.add(capitalizeCategory(product.category));
        if (Array.isArray(product.categoryPath) && product.categoryPath[1]) bucket.add(String(product.categoryPath[1]));
      }
    });
    return Array.from(bucket).sort();
  }, [products, capitalizeCategory]);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const categoryParam = params.get('category');
    const queryParam = (params.get('search') || '').trim();
    setSearchQuery(queryParam);
    if (categoryParam) {
      const legacyCategory = /^(women|women\/jewellery)$/i.test(categoryParam) ? 'Jewellery' : categoryParam;
      const formatted = capitalizeCategory(legacyCategory);
      if (formatted && (formatted === 'All' || uniqueCategories.includes(formatted))) {
        setCategoryFilter(formatted === 'All' ? 'all' : formatted);
        setPage(1);
      }
    }
  }, [location.search, uniqueCategories, capitalizeCategory]);

  React.useEffect(() => {
    let active = true;
    if (!searchQuery || sortOption === 'random') { setSearchProducts(null); setSearchTotal(null); setSearchError(null); setSearchLoading(false); return undefined; }
    setSearchLoading(true);
    setSearchError(null);
    apiClient.get('search', { params: { q: searchQuery, limit: 24 } })
      .then(({ data }) => { if (active) { setSearchProducts(Array.isArray(data) ? data : (data?.products || [])); setSearchTotal(Array.isArray(data) ? data.length : (data?.total || 0)); } })
      .catch(searchRequestError => { if (active) { setSearchProducts([]); setSearchTotal(0); setSearchError(searchRequestError); } })
      .finally(() => { if (active) setSearchLoading(false); });
    return () => { active = false; };
  }, [searchQuery, sortOption]);

  React.useEffect(() => {
    const query = searchQuery.trim();
    if (!query) return;
    const eventId = `search:${query.toLowerCase()}`;
    trackTikTokEvent('Search', { contents: [], value: 0, currency: TIKTOK_CURRENCY, search_string: query }, eventId);
    trackMetaEvent('Search', { content_type: 'product', search_string: query, value: 0, currency: TIKTOK_CURRENCY }, eventId);
  }, [searchQuery]);

  React.useEffect(() => {
    let active = true;
    if (sortOption !== 'random') {
      setRandomLoading(false);
      setRandomError(null);
      return undefined;
    }
    setRandomLoading(true);
    setRandomError(null);
    const params = {
      limit: itemsPerPage,
      page,
      seed: `${randomSeed.current}:${categoryFilter}:${searchQuery}:${viewFilter}`,
    };
    if (categoryFilter !== 'all') params.category = categoryFilter;
    if (searchQuery) params.search = searchQuery;
    if (viewFilter === 'featured') params.featured = 'true';
    if (viewFilter === 'inStock') params.availability = 'available';
    apiClient.get('products/random', { params })
      .then(({ data }) => {
        if (!active) return;
        const payload = Array.isArray(data) ? { products: data, total: data.length } : data;
        setRandomProducts(Array.isArray(payload?.products) ? payload.products : []);
        setRandomTotal(Number(payload?.total) || 0);
      })
      .catch(randomRequestError => {
        if (!active) return;
        setRandomProducts([]);
        setRandomTotal(0);
        setRandomError(randomRequestError);
      })
      .finally(() => active && setRandomLoading(false));
    return () => { active = false; };
  }, [sortOption, page, categoryFilter, searchQuery, viewFilter]);

  const filteredProducts = React.useMemo(() => {
    const sourceProducts = sortOption === 'random' ? randomProducts : (searchProducts || products);
    const base = sortOption === 'random' || categoryFilter === 'all' ? sourceProducts : sourceProducts.filter(product => capitalizeCategory(product.category) === categoryFilter || (Array.isArray(product.categoryPath) && product.categoryPath.includes(categoryFilter)));

    const sorted = [...base].sort((a, b) => {
      switch (sortOption) {
        case 'priceLow':
          return (a.price || 0) - (b.price || 0);
        case 'priceHigh':
          return (b.price || 0) - (a.price || 0);
        case 'rating':
          return (b.rating || 0) - (a.rating || 0);
        case 'newest':
          return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
        default:
          return 0;
      }
    });

    if (sortOption !== 'random' && viewFilter === 'inStock') {
      return sorted.filter(product => product?.inventoryMode === 'SUPPLIER_BASED' ? product.availabilityStatus === 'AVAILABLE_TO_ORDER' : (['AVAILABILITY_BASED', 'AVAILABILITY'].includes(product?.inventoryMode) ? product.availabilityStatus === 'IN_STOCK' : typeof product.stock === 'number' && product.stock > 0));
    }
    if (sortOption !== 'random' && viewFilter === 'featured') {
      return sorted.filter(product => product.isFeatured || (product.rating || 0) >= 4.5);
    }
    return sorted;
  }, [products, searchProducts, randomProducts, categoryFilter, sortOption, viewFilter, capitalizeCategory]);

  const pageCount = sortOption === 'random' ? Math.max(1, Math.ceil(randomTotal / itemsPerPage)) : Math.max(1, Math.ceil(filteredProducts.length / itemsPerPage));
  const startIndex = (page - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const productsToShow = sortOption === 'random' ? filteredProducts : filteredProducts.slice(startIndex, endIndex);

  const handlePageChange = (_event, value) => {
    setPage(value);
    setAnimatedCards([]);
  };

  const handleCategoryChange = event => {
    randomSeed.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setCategoryFilter(event.target.value);
    setPage(1);
    setAnimatedCards([]);
  };

  const handleSortChange = event => {
    randomSeed.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setSortOption(event.target.value);
    setPage(1);
  };

  const handleViewChange = (_event, nextView) => {
    if (nextView !== null) {
      randomSeed.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      setViewFilter(nextView);
      setPage(1);
    }
  };

  const handleQuickSelect = category => {
    randomSeed.current = `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setCategoryFilter(category);
    setPage(1);
    notify({ severity: 'info', message: `Showing ${category === 'all' ? 'all' : category} products` });
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      setAnimatedCards(productsToShow.map((_, index) => index));
    }, 100);

    return () => clearTimeout(timer);
  }, [productsToShow]);

  if (loading || searchLoading || randomLoading) {
    return (
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255, 255, 255, 0.8)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" className="luxury-page luxury-shop" sx={{ pb: 8 }}>
      <Paper className="luxury-page__hero"
        elevation={0}
        sx={{ p: { xs: 3, md: 5 }, borderRadius: 4, mb: 4, background: '#E8E1D8', border: '1px solid #C5A47E' }}
      >
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Shop
            </Typography>
            <Typography variant="body1" sx={{ mt: 1, color: 'text.secondary', maxWidth: 520 }}>
              Browse our curated collection of tech essentials. Filter by category, availability, and sort to find your perfect match.
            </Typography>
          </Box>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <ToggleButtonGroup value={viewFilter} exclusive onChange={handleViewChange} size="small">
              <ToggleButton value="all">
                <FilterAltIcon fontSize="small" sx={{ mr: 0.5 }} /> All
              </ToggleButton>
              <ToggleButton value="inStock">In stock</ToggleButton>
              <ToggleButton value="featured">Featured</ToggleButton>
            </ToggleButtonGroup>
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="sort-label">
                <SortIcon />
              </InputLabel>
              <Select labelId="sort-label" id="sort-select" value={sortOption} label="Sort" onChange={handleSortChange}>
                <MenuItem value="featured">Featured</MenuItem>
                <MenuItem value="priceLow">Price: Low to High</MenuItem>
                <MenuItem value="priceHigh">Price: High to Low</MenuItem>
                <MenuItem value="rating">Top Rated</MenuItem>
                <MenuItem value="newest">Newest</MenuItem>
                <MenuItem value="random">Random / Discover</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Paper>

      {searchQuery && <Typography variant="h5" sx={{ mb: 2 }}>Search results for “{searchQuery}” <Typography component="span" variant="body1" color="text.secondary">({searchTotal ?? 0} products)</Typography></Typography>}
      {(error || searchError || randomError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(randomError || searchError || error)?.message || 'Unable to load products right now. Please try again shortly.'}
        </Alert>
      )}

      <Paper className="luxury-page__filters" elevation={0} sx={{ p: { xs: 2, md: 3 }, borderRadius: 4, mb: 4 }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 2 }}>
          <TuneIcon color="primary" />
          <Typography variant="subtitle1" fontWeight={700}>
            Quick filters
          </Typography>
        </Stack>
        <Box className="luxury-shop__category-scroll" aria-label="Shop categories">
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip label="All" color={categoryFilter === 'all' ? 'primary' : 'default'} onClick={() => handleQuickSelect('all')} />
            {uniqueCategories.map(category => (
              <Chip key={category} label={category} color={categoryFilter === category ? 'primary' : 'default'} onClick={() => handleQuickSelect(category)} />
            ))}
          </Stack>
        </Box>
        <Divider sx={{ my: 2 }} />
        <FormControl fullWidth size="small">
          <InputLabel id="category-filter-label">Filter by Category</InputLabel>
          <Select labelId="category-filter-label" id="category-filter" value={categoryFilter} label="Filter by Category" onChange={handleCategoryChange}>
            <MenuItem value="all">All Categories</MenuItem>
            {uniqueCategories.map(category => (
              <MenuItem key={category} value={category}>
                {category}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Paper>

      <Grid container className="product-grid" spacing={0}>
        {productsToShow.map((product, index) => (
          <Grid item key={product.id} xs={6} sm={6} md={4} lg={3} xl={2} className={animatedCards.includes(index) ? 'product-card-animated' : ''}>
            <ProductCard product={product} addToCart={addToCart} />
          </Grid>
        ))}
      </Grid>

      {!productsToShow.length && !loading && (
        <Alert severity="info" sx={{ mt: 4 }}>
          No products matched your filters. Try adjusting categories or sorting options.
        </Alert>
      )}

      {pageCount > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4, mb: 3 }}>
          <Pagination count={pageCount} page={page} onChange={handlePageChange} color="primary" />
        </Box>
      )}
    </Container>
  );
}

export default Shop;
